import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ChevronRight,
    LayoutGrid,
    ShoppingCart,
    Store,
    Shirt,
    Footprints,
    Smartphone,
    Gem,
    UtensilsCrossed,
    Cookie,
    Film,
    Building2,
    Car,
    Stethoscope,
    Sparkles,
    Dumbbell,
    BookOpen,
    Sofa,
    Hotel,
    Plane,
    Grid3x3,
} from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/** Slug → Lucide icon mapping. Any unmapped slug falls back to `LayoutGrid`. */
const CATEGORY_ICON = {
    supermarkets: ShoppingCart,
    hypermarkets: Store,
    fashion: Shirt,
    footwear: Footprints,
    electronics: Smartphone,
    jewellery: Gem,
    restaurants: UtensilsCrossed,
    bakeries: Cookie,
    movies: Film,
    "shopping-malls": Building2,
    automobile: Car,
    healthcare: Stethoscope,
    beauty: Sparkles,
    fitness: Dumbbell,
    books: BookOpen,
    furniture: Sofa,
    hotels_resorts: Hotel,
    travel_tours: Plane,
};

/**
 * Left-sliding sheet with a scrollable list of ALL categories from
 * `GET /api/categories`. Tapping a row navigates to
 * `/posts?category_slug=<slug>`. Also fires a `tile_click` analytics event so
 * the admin dashboard can attribute traffic to this surface.
 */
export function CategoryDrawer({ open, onOpenChange }) {
    const nav = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || categories.length > 0) return;
        setLoading(true);
        api.get("/categories")
            .then((r) => {
                const items = Array.isArray(r.data) ? r.data : [];
                // Sort alphabetically for a predictable browse experience.
                items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                setCategories(items);
            })
            .catch(() => setCategories([]))
            .finally(() => setLoading(false));
    }, [open, categories.length]);

    const go = (cat) => {
        trackEvent("tile_click", {
            tile_slug: `category-drawer:${cat.slug}`,
            tile_label: cat.name,
            source: "category_drawer",
        });
        onOpenChange?.(false);
        nav(`/posts?category_slug=${encodeURIComponent(cat.slug)}`);
    };

    const seeAll = () => {
        trackEvent("tile_click", {
            tile_slug: "category-drawer:all",
            tile_label: "See all offers",
            source: "category_drawer",
        });
        onOpenChange?.(false);
        nav("/posts");
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="left"
                className="flex w-[85%] max-w-sm flex-col p-0"
                data-testid="category-drawer"
            >
                <SheetHeader className="border-b border-slate-100 bg-white px-6 py-5 text-left">
                    <SheetTitle className="flex items-center gap-2 font-heading text-xl font-extrabold text-[var(--text-primary)]">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFF3E6] text-[var(--brand-orange)]">
                            <Grid3x3 size={16} />
                        </span>
                        Browse Categories
                    </SheetTitle>
                    <SheetDescription className="font-body text-xs text-[var(--text-muted)]">
                        Filter offers by category.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto bg-white">
                    {loading && categories.length === 0 ? (
                        <div className="space-y-2 p-4">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="h-12 w-full animate-pulse rounded-xl bg-slate-100"
                                    data-testid={`category-skeleton-${i}`}
                                />
                            ))}
                        </div>
                    ) : (
                        <ul className="py-2">
                            {categories.map((c) => {
                                const Icon = CATEGORY_ICON[c.slug] || LayoutGrid;
                                return (
                                    <li key={c.slug}>
                                        <button
                                            onClick={() => go(c)}
                                            data-testid={`category-row-${c.slug}`}
                                            className="group flex w-full items-center gap-3 px-6 py-3 text-left transition hover:bg-[#FFF8F2]"
                                        >
                                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FFF3E6] text-[var(--brand-orange)]">
                                                <Icon size={18} />
                                            </span>
                                            <span className="flex-1 truncate font-heading text-[15px] font-semibold text-[var(--text-primary)]">
                                                {c.name}
                                            </span>
                                            <ChevronRight
                                                size={16}
                                                className="text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-[var(--brand-orange)]"
                                            />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="border-t border-slate-100 bg-white px-6 py-4">
                    <button
                        onClick={seeAll}
                        data-testid="category-drawer-see-all"
                        className="btn-primary flex w-full items-center justify-center gap-2 rounded-full py-2.5"
                    >
                        <LayoutGrid size={16} />
                        See all offers
                    </button>
                </div>
            </SheetContent>
        </Sheet>
    );
}

export default CategoryDrawer;
